import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../contexts/AuthContext';
import { useToast } from '../../Toast';
import lavenderIconLight from '../../assets/blue_unfilled_icon_light.png';
import lavenderIconDark from '../../assets/lavender_unfilled_icon_dark.png';

const API_URL = '/api';

function AccountSettings() {
  const { user, darkMode, toggleDarkMode, logout } = useContext(AuthContext);
  const toast = useToast();
  const navigate = useNavigate();

  // Profile update state
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAccount, setLoadingAccount] = useState(true);

  // Load account data
  useEffect(() => {
    loadAccountData();
  }, []);

  const loadAccountData = async () => {
    try {
      const response = await axios.get(`${API_URL}/account`);
      setUsername(response.data.username);
      setEmail(response.data.email);
      setLoadingAccount(false);
    } catch (err) {
      console.error('Failed to load account data:', err);
      toast.error('Failed to load account information');
      setLoadingAccount(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API_URL}/account`, {
        username,
        email
      });

      toast.success(response.data.message || 'Profile updated successfully');

      if (response.data.email_verification_required) {
        toast.info('Please check your email to verify your new email address');
      }

      loadAccountData();
    } catch (err) {
      console.error('Profile update error:', err);
      toast.error(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/account/export`);

      // Create a downloadable JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ref-lex-account-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Account data exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error(err.response?.data?.error || 'Failed to export account data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmText = 'DELETE';
    const userInput = prompt(
      `⚠️ WARNING: This action is IRREVERSIBLE!\n\n` +
      `Deleting your account will permanently delete:\n` +
      `• All your projects\n` +
      `• All your references and annotations\n` +
      `• All your categories\n` +
      `• Your collaboration invitations\n\n` +
      `Type "${confirmText}" (in capital letters) to confirm deletion:`
    );

    if (userInput !== confirmText) {
      if (userInput !== null) {
        toast.error('Account deletion cancelled');
      }
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_URL}/account`);
      toast.success('Account deleted successfully');

      // Logout and redirect
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 1500);
    } catch (err) {
      console.error('Delete account error:', err);
      toast.error(err.response?.data?.error || 'Failed to delete account');
      setLoading(false);
    }
  };

  if (loadingAccount) {
    return (
      <div className="container">
        <div className="header">
          <Link to="/" className="back-link">← Back to Projects</Link>
          <h2>Account Settings</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Loading account information...
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <Link to="/" className="back-link">← Back to Projects</Link>
        <h1>
          <img src={darkMode ? lavenderIconDark : lavenderIconLight} alt="Ref-Lex Icon" style={{ height: '2.7em', verticalAlign: 'bottom', marginRight: '0.3em' }} />
          <span className="logo-text">Account Settings</span>
        </h1>
        <div className="header-actions">
          <button onClick={toggleDarkMode} className="theme-toggle" title="Toggle dark mode">
            {darkMode ? '☀️' : '🕶️'}
          </button>
        </div>
      </div>

      <div className="account-settings-container">
        {/* Profile Update Section */}
        <section className="settings-section">
          <h2>Profile Information</h2>
          <p className="section-description">Update your username and email address</p>

          <form onSubmit={handleUpdateProfile} className="settings-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                minLength="3"
                maxLength="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
              <small className="form-hint">
                Changing your email will require re-verification
              </small>
            </div>

            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>
        </section>

        {/* Data Export Section */}
        <section className="settings-section">
          <h2>Export Your Data</h2>
          <p className="section-description">
            Download all your account data including projects, references, and annotations in JSON format
          </p>

          <button
            onClick={handleExportData}
            disabled={loading}
            className="btn-secondary"
          >
            {loading ? 'Exporting...' : '📥 Export Account Data'}
          </button>
        </section>

        {/* Account Deletion Section */}
        <section className="settings-section danger-section">
          <h2>Danger Zone</h2>
          <p className="section-description">
            Once you delete your account, there is no going back. This action is permanent and cannot be undone.
          </p>

          <div className="danger-warning">
            ⚠️ <strong>Warning:</strong> Deleting your account will permanently delete all your projects, references, annotations, and categories.
          </div>

          <button
            onClick={handleDeleteAccount}
            disabled={loading}
            className="btn-danger"
          >
            {loading ? 'Deleting...' : '🗑️ Delete Account Permanently'}
          </button>
        </section>
      </div>
    </div>
  );
}

export default AccountSettings;
